import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { VAT_REGIME_OPTIONS } from "../../lib/invoiceConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const FIELDS = [
  ["name", "Raison sociale"],
  ["legalForm", "Forme juridique (SARL, SAS, EI...)"],
  ["capital", "Capital social"],
  ["address", "Adresse"],
  ["postalCode", "Code postal"],
  ["city", "Ville"],
  ["siren", "SIREN (9 chiffres)"],
  ["siret", "SIRET (14 chiffres)"],
  ["tvaIntra", "N° TVA intracommunautaire"],
  ["rcs", "RCS"],
  ["ape", "Code APE / NAF"],
  ["phone", "Téléphone"],
  ["email", "Email"],
  ["iban", "IBAN"],
  ["bic", "BIC"],
];

const CompanySettingsDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    API.get("/settings/company")
      .then(({ data }) => setForm(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put("/settings/company", form);
      toast.success("Paramètres société enregistrés");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-settings-dialog">
        <DialogHeader>
          <DialogTitle>Paramètres société (mentions légales)</DialogTitle>
          <DialogDescription>
            Ces informations apparaissent sur les factures. Renseignez SIREN/SIRET, TVA et RCS pour la conformité.
          </DialogDescription>
        </DialogHeader>

        {loading || !form ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(207,78%,42%)]" /></div>
        ) : (
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS.map(([key, label]) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <Input value={form[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`company-${key}`} />
                </div>
              ))}
              <div className="space-y-1">
                <Label className="text-xs">Régime TVA</Label>
                <Select value={form.vatRegime || "franchise"} onValueChange={(v) => set("vatRegime", v)}>
                  <SelectTrigger data-testid="company-vatRegime"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VAT_REGIME_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Conditions de paiement</Label>
                <Input value={form.defaultPaymentTerms || ""} onChange={(e) => set("defaultPaymentTerms", e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="company-settings-save">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanySettingsDialog;
